/**
 * PDF Export Service
 * Generate PDF documents for tournament data
 */
import jsPDF from 'jspdf';
import 'jspdf-autotable';

class PDFExportService {
  /**
   * Generate tournament PDF
   * @param {Object} tournamentData - Tournament data including name, state, config
   * @param {Object} options - Export options (includeRanking, includeSchedule, includeResults)
   */
  generateTournamentPDF(tournamentData, options = {}) {
    const {
      includeRanking = true,
      includeSchedule = true,
      includeResults = true
    } = options;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let yPosition = 20;

    // Header
    yPosition = this.addHeader(doc, tournamentData, pageWidth, yPosition);

    // Tournament Info
    yPosition = this.addTournamentInfo(doc, tournamentData, pageWidth, yPosition);

    // Ranking
    if (includeRanking && tournamentData.state?.players?.length > 0) {
      yPosition = this.addRanking(doc, tournamentData.state.players, pageWidth, yPosition);
    }

    // Schedule
    if (includeSchedule && tournamentData.state?.schedule?.rounds?.length > 0) {
      yPosition = this.addSchedule(doc, tournamentData.state, pageWidth, yPosition);
    }

    // Results (completed matches)
    if (includeResults && tournamentData.state?.schedule?.rounds?.length > 0) {
      this.addResults(doc, tournamentData.state, pageWidth, yPosition);
    }

    // Footer
    this.addFooter(doc);

    // Generate filename
    const fileName = this.generateFileName(tournamentData.name);

    // Save PDF
    doc.save(fileName);
  }

  /**
   * Add header to PDF
   */
  addHeader(doc, tournamentData, pageWidth, yPosition) {
    // Title
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Up & Down - Tournoi Padel', pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 10;

    // Tournament name
    doc.setFontSize(16);
    doc.setFont(undefined, 'normal');
    doc.text(tournamentData.name || 'Tournoi sans nom', pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 15;

    return yPosition;
  }

  /**
   * Add tournament info section
   */
  addTournamentInfo(doc, tournamentData, pageWidth, yPosition) {
    const config = tournamentData.state?.config || {};
    const players = tournamentData.state?.players || [];

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    const info = [
      `Joueurs: ${config.playerCount || players.length || '-'}`,
      `Terrains: ${config.courtCount || '-'}`,
      `Durée match: ${config.matchDuration || '-'} min`,
      `Sets gagnants: ${config.setsToWin || 1}`,
      `Date: ${new Date().toLocaleDateString('fr-FR')}`
    ];

    const infoText = info.join('  •  ');
    doc.text(infoText, pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 15;

    return yPosition;
  }

  /**
   * Add ranking table
   */
  addRanking(doc, players, pageWidth, yPosition) {
    // Check if new page needed
    if (yPosition > 220) {
      doc.addPage();
      yPosition = 20;
    }

    // Section title
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('🏆 Classement', 14, yPosition);

    yPosition += 8;

    // Sort players
    const sortedPlayers = [...players].sort((a, b) => {
      if (b.points !== a.points) return b.points - a.points;
      if (b.setsWon !== a.setsWon) return b.setsWon - a.setsWon;
      if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
      if (a.gamesLost !== b.gamesLost) return a.gamesLost - b.gamesLost;
      return (a.name || '').localeCompare(b.name || '');
    });

    // Prepare table data
    const tableData = sortedPlayers.map((player, index) => {
      let rankDisplay = index + 1;
      if (index === 0) rankDisplay = '🥇';
      else if (index === 1) rankDisplay = '🥈';
      else if (index === 2) rankDisplay = '🥉';

      return [
        rankDisplay,
        player.name || `Joueur ${player.id}`,
        player.points || 0,
        player.gamesWon || 0,
        player.gamesLost || 0,
        player.wins || 0,
        player.draws || 0,
        player.losses || 0
      ];
    });

    // Generate table
    doc.autoTable({
      startY: yPosition,
      head: [['Rang', 'Joueur', 'Pts', 'Jeu+', 'Jeu-', 'V', 'N', 'D']],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [37, 99, 235], // Blue
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      bodyStyles: {
        halign: 'center'
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 20 },
        1: { halign: 'left', cellWidth: 50 }
      }
    });

    return doc.lastAutoTable.finalY + 15;
  }

  /**
   * Add schedule section
   */
  addSchedule(doc, state, pageWidth, yPosition) {
    const schedule = state.schedule || {};
    const rounds = schedule.rounds || [];

    if (rounds.length === 0) return yPosition;

    // Check if new page needed
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }

    // Section title
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('📅 Planning des matchs', 14, yPosition);

    yPosition += 10;

    rounds.forEach((round, roundIndex) => {
      // Check if new page needed
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }

      // Round title
      doc.setFontSize(12);
      doc.setFont(undefined, 'bold');
      doc.text(`Tour ${roundIndex + 1}`, 14, yPosition);

      yPosition += 6;

      // Matches table
      const matchesData = (round.matches || []).map((match, matchIndex) => {
        const team1Names = this.getPlayerNames(match.team1, state.players);
        const team2Names = this.getPlayerNames(match.team2, state.players);
        const score = this.formatMatchScore(match);

        return [
          `Terrain ${matchIndex + 1}`,
          team1Names,
          'vs',
          team2Names,
          score
        ];
      });

      if (matchesData.length > 0) {
        doc.autoTable({
          startY: yPosition,
          head: [['Court', 'Équipe 1', '', 'Équipe 2', 'Score']],
          body: matchesData,
          theme: 'striped',
          headStyles: {
            fillColor: [100, 116, 139],
            fontSize: 9
          },
          bodyStyles: {
            fontSize: 9
          },
          columnStyles: {
            0: { cellWidth: 25 },
            2: { halign: 'center', cellWidth: 15 },
            4: { halign: 'center', cellWidth: 30 }
          }
        });

        yPosition = doc.lastAutoTable.finalY + 8;
      }
    });

    return yPosition + 5;
  }

  /**
   * Add results section (completed matches only)
   */
  addResults(doc, state, pageWidth, yPosition) {
    const schedule = state.schedule || {};
    const rounds = schedule.rounds || [];

    // Get completed matches
    const completedMatches = [];
    rounds.forEach((round, roundIndex) => {
      (round.matches || []).forEach((match, matchIndex) => {
        if (match.completed || match.detailedScore?.isComplete) {
          completedMatches.push({
            ...match,
            roundIndex,
            matchIndex
          });
        }
      });
    });

    if (completedMatches.length === 0) return yPosition;

    // Check if new page needed
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }

    // Section title
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('📊 Résultats', 14, yPosition);

    yPosition += 8;

    // Results table
    const resultsData = completedMatches.map((match) => {
      const team1Names = this.getPlayerNames(match.team1, state.players);
      const team2Names = this.getPlayerNames(match.team2, state.players);
      const score = this.formatMatchScore(match);

      return [
        `Tour ${match.roundIndex + 1}`,
        team1Names,
        team2Names,
        score
      ];
    });

    doc.autoTable({
      startY: yPosition,
      head: [['Tour', 'Équipe 1', 'Équipe 2', 'Score']],
      body: resultsData,
      theme: 'grid',
      headStyles: {
        fillColor: [34, 197, 94], // Green
        textColor: 255,
        fontStyle: 'bold'
      },
      bodyStyles: {
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 25 },
        3: { halign: 'center', cellWidth: 35, fontStyle: 'bold' }
      }
    });

    return doc.lastAutoTable.finalY + 10;
  }

  /**
   * Add footer to all pages
   */
  addFooter(doc) {
    const pageCount = doc.internal.getNumberOfPages();
    const pageHeight = doc.internal.pageSize.getHeight();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(128);

      // Left footer: "Propulsé par FindPlayers"
      doc.text('Propulsé par FindPlayers', 14, pageHeight - 10);

      // Right footer: Page number
      doc.text(`Page ${i} sur ${pageCount}`, doc.internal.pageSize.getWidth() - 14, pageHeight - 10, { align: 'right' });
    }
  }

  /**
   * Get player names from IDs
   */
  getPlayerNames(playerIds, players) {
    if (!players || !playerIds) return '?';

    const playersArray = Array.isArray(players) ? players : Object.values(players);

    return playerIds.map(id => {
      const player = playersArray.find(p => p.id === id);
      return player ? player.name : `Joueur ${id}`;
    }).join(' / ');
  }

  /**
   * Format match score
   */
  formatMatchScore(match) {
    if (!match.detailedScore || !match.detailedScore.sets || match.detailedScore.sets.length === 0) {
      if (match.completed) {
        return match.winner ? `Équipe ${match.winner}` : 'Nul';
      }
      return '-';
    }

    // Count sets won
    let team1Sets = 0;
    let team2Sets = 0;

    match.detailedScore.sets.forEach(set => {
      const t1 = set.team1Games || set.team1 || 0;
      const t2 = set.team2Games || set.team2 || 0;

      if (t1 > t2) team1Sets++;
      else if (t2 > t1) team2Sets++;
    });

    // Format set scores
    const setScores = match.detailedScore.sets.map(set => {
      const t1 = set.team1Games || set.team1 || 0;
      const t2 = set.team2Games || set.team2 || 0;
      let score = `${t1}-${t2}`;

      if (set.tiebreak || set.tiebreakScore) {
        const tb = set.tiebreak || set.tiebreakScore;
        const tb1 = tb.team1Points || tb.team1 || 0;
        const tb2 = tb.team2Points || tb.team2 || 0;
        score += ` (${tb1}-${tb2})`;
      }

      return score;
    }).join(', ');

    return `${team1Sets}-${team2Sets} (${setScores})`;
  }

  /**
   * Generate filename for PDF
   */
  generateFileName(tournamentName) {
    const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    const safeName = (tournamentName || 'tournoi')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return `updown-${safeName}-${date}.pdf`;
  }
}

// Export singleton instance
const pdfExportService = new PDFExportService();
export default pdfExportService;
